import React, { useEffect, useState } from "react";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";
import { useQuery } from "@tanstack/react-query";

import { Box } from "@mui/material";
import "../BrandMetadataReview.css";
import BrandReviewInstructions from "../UI/BrandReviewInstructions";

import { GridColDef } from "@mui/x-data-grid-premium";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";
import { renderNumberCell } from "utils/dataGrid";

interface BrandMetadataReviewProps {
  chain_id: string;
  country_code: string;
}

const columns_def: GridColDef[] = [
  {
    field: "chain_id",
    headerName: "Chain ID",
    type: "string",
    width: 200,
  },
  {
    field: "metadata_category",
    headerName: "Metadata Category",
    description: "Business category from a list in brand metadata",
    type: "string",
    width: 200,
  },
  {
    field: "actual_category",
    headerName: "Places Category",
    description:
      "Business category associated with the brand and country in the places table.",
    type: "string",
    width: 200,
  },
  {
    field: "business_category_count",
    headerName: "Business Category Count",
    description:
      "The count of places associated with this brand, country and business category.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "category_type",
    headerName: "Category Type",
    description: "Business category classification (i.e. main or child).",
    type: "string",
    width: 150,
  },
];

const ReviewBrandBizCatsXCountries: React.FC<BrandMetadataReviewProps> = ({
  chain_id,
  country_code,
}) => {
  const { showError } = useError();
  const { apiRequest } = useApi();

  const { data, isLoading: loading } = useQuery<any[]>({
    queryKey: ["business_categories_by_country", chain_id, country_code],
    queryFn: async () => {
      try {
        const params = new URLSearchParams({
          chain_id: chain_id,
          country_code: country_code,
        });

        const response = await apiRequest<any[]>(
          `/brand_metadata/business_categories_by_country?${params.toString()}`,
          { method: "GET" }
        );

        if (response.data) {
          return response.data;
        } else {
          throw new Error("No data found");
        }
      } catch (error) {
        if (error instanceof Error) {
          showError(
            `Failed to fetch business categories to review for ${chain_id} + ${country_code}.`
          );
        } else {
          showError("Failed to fetch countries: Unknown error");
        }
        throw new Error("No data found");
      }
    },
    enabled: !!chain_id && !!country_code,
  });

  const user_instructions = {
    section_id: "brand-country-business-categories",
    instruction_title: "Review Business Categories",
    purpose:
      "Accurate business category metadata is crucial for ensuring high-quality results in our chain identification process. This process associates places in our database with specific brands by assigning a unique `chain_id` value to each brand. This `chain_id` is consistent across all places associated with the brand in our database, ensuring accurate grouping and identification. From a customer perspective, business categories enable users to filter and group places by type, improving usability and insights.",
    goal: "Ensure all business categories for the brand, associated with the selected country of operation, are accurately reflected in the metadata.",
    task_description:
      "The table below compares the business categories listed in the `business_category_ids` attribute in the metadata with the business categories linked to places in the dataplor database for the brand.",
    task_steps: [
      {
        title: "Identify Missing Business Categories",
        instructions: [
          "Review the table below to identify any business categories associated with places in the database that are missing from the metadata.",
        ],
      },
      {
        title: "Validate Missing Business Categories",
        instructions: [
          "Research and verify whether the missing business categories are applicable to the brand for the selected country of operation.",
        ],
      },
      {
        title: "Update Metadata",
        instructions: [
          {
            text: "For validated business categories, update the `business_category_ids` attribute in `Kuebiko`:",
            sublist: [
              "Navigate to the brand in `Kuebiko`.",
              "Select `Brand Edit`.",
              "Update the metadata to include the missing business categories.",
            ],
          },
        ],
      },
    ],
  };

  return (
    <Box className="metadata-review-container">
      <BrandReviewInstructions {...user_instructions} />
      <GaneshDataGrid
        gridId="reviewBizCatsByCountryGrid"
        columns={columns_def}
        data={data}
        loading={loading}
        headerFilters={data && data.length > 0 ? true : false}
        customHeight={(data?.length ?? 0) > 0 ? "750px" : "300px"}
      />
    </Box>
  );
};

export default ReviewBrandBizCatsXCountries;
